import React, { useState, useEffect, useMemo } from 'react';
import { 
  Row, Col, Card, Button, Table, Badge, Form, InputGroup, Alert, Spinner, Modal
} from 'react-bootstrap';
import { 
  FaCalendarCheck, FaSearch, FaFilter, FaEye, FaSync, FaEdit, FaTrash, FaCalendarAlt,
  FaSortUp, FaSortDown, FaSort
} from 'react-icons/fa';
import { useApi } from '../context/ApiContext';

const Appointments = () => {
  const { apiCall } = useApi();
  
  // States
  const [allAppointments, setAllAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    city: 'all',
    dateRangeStart: '',
    dateRangeEnd: ''
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'start_date', // user, city, start_date, end_date, duration
    direction: 'asc' // asc, desc
  });
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    start_date: '',
    end_date: ''
  });
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Tek API çağrısıyla TÜM aktif randevuları al (ÇOK HIZLI!)
      const response = await apiCall('GET', '/api/user-appointments/all/active');
      const appointments = (response.data || []).map(apt => ({
        id: apt.id,
        user_id: apt.user_id,
        city_id: apt.city_id,
        start_date: apt.start_date,
        end_date: apt.end_date,
        is_active: apt.is_active,
        user_info: {
          id: apt.user_id,
          name: apt.applicant_name || apt.username,
          visa_class: apt.visa_class,
          appointment_city: apt.appointment_city
        }
      }));
      
      setAllAppointments(appointments);
      setError(null);
    } catch (err) {
      console.error('Error loading appointments data:', err);
      setError(err.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Helper function for calculating duration
  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  // Client-side filtering and sorting with useMemo (ultra-fast, no page reload!)
  const appointments = useMemo(() => {
    let filtered = [...allAppointments];
    
    // Apply search filter
    if (filters.search) {
      filtered = filtered.filter(appointment =>
        appointment.user_info.name?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    
    // Apply city filter
    if (filters.city !== 'all') {
      filtered = filtered.filter(appointment =>
        appointment.user_info.appointment_city === filters.city
      );
    }
    
    // Apply date range filter
    if (filters.dateRangeStart || filters.dateRangeEnd) {
      filtered = filtered.filter(appointment => {
        const aptStart = new Date(appointment.start_date);
        const aptEnd = new Date(appointment.end_date);
        
        // Sadece başlangıç tarihi girilmişse: O tarihten sonraki randevular
        if (filters.dateRangeStart && !filters.dateRangeEnd) {
          const rangeStart = new Date(filters.dateRangeStart);
          return aptEnd >= rangeStart; // Randevu bitiş tarihi, seçilen tarihten sonra/eşit
        }
        
        // Sadece bitiş tarihi girilmişse: O tarihten önceki randevular
        if (!filters.dateRangeStart && filters.dateRangeEnd) {
          const rangeEnd = new Date(filters.dateRangeEnd);
          return aptStart <= rangeEnd; // Randevu başlangıç tarihi, seçilen tarihten önce/eşit
        }
        
        // Her ikisi de girilmişse: Aralık ile kesişen randevular
        if (filters.dateRangeStart && filters.dateRangeEnd) {
          const rangeStart = new Date(filters.dateRangeStart);
          const rangeEnd = new Date(filters.dateRangeEnd);
          return aptStart <= rangeEnd && aptEnd >= rangeStart;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortConfig.key) {
        case 'user':
          comparison = (a.user_info.name || '').localeCompare(b.user_info.name || '', 'tr');
          break;
        case 'city':
          comparison = (a.user_info.appointment_city || '').localeCompare(b.user_info.appointment_city || '', 'tr');
          break;
        case 'start_date':
          comparison = new Date(a.start_date) - new Date(b.start_date);
          break;
        case 'end_date':
          comparison = new Date(a.end_date) - new Date(b.end_date);
          break;
        case 'duration':
          const durationA = calculateDuration(a.start_date, a.end_date);
          const durationB = calculateDuration(b.start_date, b.end_date);
          comparison = durationA - durationB;
          break;
        default:
          comparison = 0;
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [allAppointments, filters, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="ms-1 text-muted" size={12} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1 text-primary" size={12} />
      : <FaSortDown className="ms-1 text-primary" size={12} />;
  };

  const handleViewDetails = async (appointment) => {
    setSelectedAppointment(appointment);
    setSelectedUser(appointment.user_info);
    setShowDetailsModal(true);
  };

  const handleEdit = (appointment) => {
    setSelectedAppointment(appointment);
    setEditFormData({
      start_date: appointment.start_date,
      end_date: appointment.end_date
    });
    setShowEditModal(true);
  };

  const handleDelete = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDeleteModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.start_date || !editFormData.end_date) {
      alert('Lütfen başlangıç ve bitiş tarihlerini seçin');
      return;
    }

    if (new Date(editFormData.end_date) < new Date(editFormData.start_date)) {
      alert('Bitiş tarihi başlangıç tarihinden önce olamaz');
      return;
    }

    try {
      setSaveLoading(true);
      await apiCall('PUT', `/api/user-appointments/${selectedAppointment.id}`, {
        start_date: editFormData.start_date,
        end_date: editFormData.end_date
      });
      
      setShowEditModal(false);
      setEditFormData({ start_date: '', end_date: '' });
      setSelectedAppointment(null);
      await loadData();
    } catch (err) {
      alert(err.message || 'Randevu güncellenirken hata oluştu');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSaveLoading(true);
      await apiCall('DELETE', `/api/user-appointments/${selectedAppointment.id}`);
      
      setShowDeleteModal(false);
      setSelectedAppointment(null);
      await loadData();
    } catch (err) {
      alert(err.message || 'Randevu silinirken hata oluştu');
    } finally {
      setSaveLoading(false);
    }
  };

  const getCityBadge = (city) => {
    const cityMap = {
      'Istanbul': { bg: 'primary', text: 'İstanbul' },
      'Ankara': { bg: 'success', text: 'Ankara' }
    };
    return cityMap[city] || { bg: 'secondary', text: city };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-4">
        <Row className="align-items-start">
          <Col lg={9}>
            <h1 className="h3 mb-2">
              <FaCalendarCheck className="me-2 text-primary" />
              Aranılan Tarihler
            </h1>
            <p className="text-muted mb-2">
              Hangi kullanıcılar hangi tarihlerde randevu arıyor?
            </p>
            <Alert variant="info" className="py-2 px-3 mb-0">
              <small>
                <strong>💡 Not:</strong> Sadece aktif tarih aralıkları gösterilir.
                {' • '}Yeni aralık eklemek için <strong>Kullanıcılar {'>'} Tarih Aralıkları</strong> kullanın.
              </small>
            </Alert>
          </Col>
          <Col lg={3} className="text-lg-end mt-3 mt-lg-0">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              <FaSync className={`me-1 ${loading ? 'fa-spin' : ''}`} size={12} />
              Yenile
            </Button>
          </Col>
        </Row>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <strong>Hata:</strong> {error}
        </Alert>
      )}
      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">Kullanıcı Adı</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Kullanıcı adı ara..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </InputGroup>
            </Col>
            
            <Col md={2}>
              <Form.Label className="small text-muted mb-1">Şehir</Form.Label>
              <Form.Select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              >
                <option value="all">Tüm Şehirler</option>
                <option value="Istanbul">İstanbul</option>
                <option value="Ankara">Ankara</option>
              </Form.Select>
            </Col>
            
            <Col md={3}>
              <Form.Label className="small text-muted mb-1 d-flex align-items-center">
                <FaCalendarAlt className="me-1" size={11} />
                Tarih Filtresi <span className="ms-1" style={{ fontSize: '10px' }}>(tek tarih de girebilirsiniz)</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="date"
                  placeholder="Başlangıç"
                  value={filters.dateRangeStart}
                  onChange={(e) => handleFilterChange('dateRangeStart', e.target.value)}
                  max={filters.dateRangeEnd || undefined}
                  title="Bu tarihten sonraki randevuları göster"
                />
                <InputGroup.Text className="px-2">-</InputGroup.Text>
                <Form.Control
                  type="date"
                  placeholder="Bitiş"
                  value={filters.dateRangeEnd}
                  onChange={(e) => handleFilterChange('dateRangeEnd', e.target.value)}
                  min={filters.dateRangeStart || undefined}
                  title="Bu tarihten önceki randevuları göster"
                />
              </InputGroup>
            </Col>
            
            <Col md={2}>
              {(filters.dateRangeStart || filters.dateRangeEnd) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, dateRangeStart: '', dateRangeEnd: '' }));
                  }}
                  className="w-100"
                >
                  Tarihi Temizle
                </Button>
              )}
            </Col>
            
            <Col md={2}>
              <div className="text-muted small d-flex align-items-center justify-content-end">
                <FaFilter className="me-1" />
                <strong>{appointments.length}</strong>&nbsp;sonuç
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Appointments Table */}
      <Card>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th 
                    onClick={() => handleSort('user')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="text-nowrap"
                  >
                    Kullanıcı {getSortIcon('user')}
                  </th>
                  <th 
                    onClick={() => handleSort('city')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="text-nowrap"
                  >
                    Şehir {getSortIcon('city')}
                  </th>
                  <th 
                    onClick={() => handleSort('start_date')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="text-nowrap"
                  >
                    Başlangıç Tarihi {getSortIcon('start_date')}
                  </th>
                  <th 
                    onClick={() => handleSort('end_date')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="text-nowrap"
                  >
                    Bitiş Tarihi {getSortIcon('end_date')}
                  </th>
                  <th 
                    onClick={() => handleSort('duration')} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="text-center text-nowrap"
                  >
                    Süre {getSortIcon('duration')}
                  </th>
                  <th className="text-center">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map(appointment => {
                  const cityBadge = getCityBadge(appointment.user_info.appointment_city);
                  const duration = calculateDuration(appointment.start_date, appointment.end_date);
                  
                  return (
                    <tr key={appointment.id}>
                      <td>
                        <div>
                          <div className="fw-medium">{appointment.user_info.name}</div>
                          <small className="text-muted">
                            {appointment.user_info.visa_class}
                          </small>
                        </div>
                      </td>
                      <td>
                        <Badge bg={cityBadge.bg}>{cityBadge.text}</Badge>
                      </td>
                      <td>
                        <div className="text-nowrap">{formatDate(appointment.start_date)}</div>
                      </td>
                      <td>
                        <div className="text-nowrap">{formatDate(appointment.end_date)}</div>
                      </td>
                      <td className="text-center">
                        <span className="fw-medium">{duration}</span>
                        <small className="text-muted ms-1">gün</small>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewDetails(appointment)}
                            title="Detayları Görüntüle"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                          >
                            <FaEye size={14} />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(appointment)}
                            title="Düzenle"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                          >
                            <FaEdit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(appointment)}
                            title="Sil"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px', padding: '0', border: '1.5px solid' }}
                          >
                            <FaTrash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          
          {appointments.length === 0 && (
            <div className="text-center py-5">
              <FaCalendarCheck size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Aktif Tarih Aralığı Bulunamadı</h5>
              <p className="text-muted mb-4">
                {filters.search || filters.city !== 'all' || filters.dateRangeStart || filters.dateRangeEnd
                  ? 'Arama kriterlerinize uygun aktif tarih aralığı bulunamadı.' 
                  : 'Henüz aktif tarih aralığı eklenmemiş veya tüm tarih aralıkları süresi dolmuş.'}
              </p>
              <small className="text-muted">
                <strong>Kullanıcılar</strong> sayfasından yeni tarih aralığı ekleyebilirsiniz.
              </small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Randevu Arama Detayları</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && selectedUser && (
            <div>
              {/* User Info */}
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">
                    <FaCalendarCheck className="me-2" />
                    Kullanıcı Bilgileri
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <div>
                        <strong>Kullanıcı Adı:</strong> {selectedUser.name}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <strong>Vize Sınıfı:</strong> {selectedUser.visa_class}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Appointment Info */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">
                    <FaCalendarCheck className="me-2" />
                    Randevu Bilgileri
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <div>
                        <strong>Şehir:</strong>
                        <Badge bg={getCityBadge(selectedUser.appointment_city).bg} className="ms-2">
                          {getCityBadge(selectedUser.appointment_city).text}
                        </Badge>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <strong>Başlangıç:</strong> {formatDate(selectedAppointment.start_date)}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <strong>Bitiş:</strong> {formatDate(selectedAppointment.end_date)}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <strong>Süre:</strong> {calculateDuration(selectedAppointment.start_date, selectedAppointment.end_date)} gün
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => !saveLoading && setShowEditModal(false)} backdrop={saveLoading ? 'static' : true} size="lg">
        <Modal.Header closeButton={!saveLoading}>
          <Modal.Title>
            <FaEdit className="me-2" />
            Tarih Aralığını Düzenle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && (
            <div>
              <Alert variant="info" className="mb-4">
                <strong>Kullanıcı Adı:</strong> {selectedAppointment.user_info?.name}
                {' • '}
                <strong>Şehir:</strong> {selectedAppointment.user_info?.appointment_city}
              </Alert>
              
              <Form>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FaCalendarAlt className="me-2 text-primary" />
                        Başlangıç Tarihi *
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={editFormData.start_date}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, start_date: e.target.value }))}
                        disabled={saveLoading}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        <FaCalendarAlt className="me-2 text-danger" />
                        Bitiş Tarihi *
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={editFormData.end_date}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, end_date: e.target.value }))}
                        min={editFormData.start_date}
                        disabled={saveLoading}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                {editFormData.start_date && editFormData.end_date && (
                  <Alert variant="light" className="mt-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>Tarih Aralığı:</strong> {formatDate(editFormData.start_date)} - {formatDate(editFormData.end_date)}
                      </div>
                      <div>
                        <strong>Süre:</strong> {calculateDuration(editFormData.start_date, editFormData.end_date)} gün
                      </div>
                    </div>
                  </Alert>
                )}
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={saveLoading}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={saveLoading}>
            {saveLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Kaydediliyor...
              </>
            ) : (
              'Kaydet'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !saveLoading && setShowDeleteModal(false)} backdrop={saveLoading ? 'static' : true} size="lg">
        <Modal.Header closeButton={!saveLoading}>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Tarih Aralığını Sil
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && (
            <div>
              <Alert variant="warning">
                <strong>⚠️ Dikkat!</strong> Bu işlem geri alınamaz.
              </Alert>
              <p>
                <strong>{selectedAppointment.user_info?.name}</strong> kullanıcısının aşağıdaki tarih aralığını silmek istediğinizden emin misiniz?
              </p>
              <Card className="bg-light">
                <Card.Body>
                  <div className="mb-2">
                    <strong>Başlangıç:</strong> {formatDate(selectedAppointment.start_date)}
                  </div>
                  <div className="mb-2">
                    <strong>Bitiş:</strong> {formatDate(selectedAppointment.end_date)}
                  </div>
                  <div>
                    <strong>Süre:</strong> {calculateDuration(selectedAppointment.start_date, selectedAppointment.end_date)} gün
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={saveLoading}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={saveLoading}>
            {saveLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Siliniyor...
              </>
            ) : (
              'Sil'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Appointments; 